import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import './biz-select.scss';
import { NativeSelect, SxProps } from '@mui/material';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FormFieldConfig } from '../../Hooks/useForm';
import { getGuid } from '../../Utils/stringUtils';
import { BizControlSize } from '../../Types/controlSize';

type BizSelectProps<OptionType> = {
	size?: BizControlSize;
	label?: string;
	options: OptionType[];
	onChange: (option: OptionType | null) => void;
	className?: string;
	selectedId: number | string | null | undefined;
	autoSelectSingleOption?: boolean;
	disabled?: boolean;
	noSelectText?: string;
	onOpen?: () => void;
	onClose?: () => void;
	getName?: (option: OptionType) => string;
	getDescription?: (option: OptionType) => string;
	getDisabled?: (option: OptionType) => boolean;
	formConfig?: FormFieldConfig;
	name?: string;
	maxOpenedWidth?: number;
	maxWidth?: number;
};

export function BizSelect<OptionType extends { Id: number | string; Name?: string } | { id: number | string; name?: string }>({
	options,
	label,
	onChange,
	selectedId,
	autoSelectSingleOption,
	disabled,
	noSelectText,
	className,
	onOpen,
	onClose,
	getName,
	getDescription,
	getDisabled,
	formConfig,
	name: pName,
	maxOpenedWidth,
	maxWidth,
	size,
}: BizSelectProps<OptionType>) {
	const anchor = useRef<HTMLDivElement>(null);
	const [name] = useState(pName ?? getGuid());
	const isValid = formConfig?.state.fields[name]?.isValid ?? true;

	useEffect(() => {
		return () => {
			//remove field from form state on unmount
			if (formConfig) {
				console.log('removing field from form state:', name);
				formConfig.state.removeField(name);
			}
		};
	}, []);

	useEffect(() => {
		if (!formConfig || !formConfig.validators) return;

		const value = selectedId && selectedId != '-1' ? selectedId.toString() : '';
		const newIsValid = formConfig.state.validateFieldValue(value, formConfig.validators);
		if (newIsValid !== isValid) {
			formConfig.state.setFieldValid(name, newIsValid, formConfig.group);
		}
	}, [selectedId, formConfig?.validators]);

	const getIdValue = (option: OptionType) => {
		return 'Id' in option ? option.Id : option.id;
	};

	const getNameValue = (option: OptionType) => {
		return 'Id' in option ? option.Name : option.name;
	};

	const shouldAutoSelectSingleOption = autoSelectSingleOption && options.length === 1;
	const singleOption = shouldAutoSelectSingleOption ? options[0] : null;

	useEffect(() => {
		if (!singleOption) return;

		const selectedOptionId = String(getIdValue(singleOption));
		const currentSelectedId = selectedId == null ? null : String(selectedId);

		if (currentSelectedId !== selectedOptionId) {
			onChange(singleOption);
			if (formConfig) {
				formConfig.state.setDirty();
			}
		}
	}, [singleOption, selectedId, onChange, formConfig]);

	function onChangeHandler(event: SelectChangeEvent) {
		const selectedOptionId = event.target.value;
		console.log('selectedOptionId', selectedOptionId);
		const option = options.find((o) => getIdValue(o) == selectedOptionId);
		console.log('selected option', option);
		onChange(
			options.find((o) => getIdValue(o) === (typeof getIdValue(o) === 'number' ? +selectedOptionId : selectedOptionId)) ??
				null
		);

		if (formConfig) {
			formConfig.state.setDirty();
		}
	}

	const getSelectedValue = () => {
		if (singleOption) {
			return String(getIdValue(singleOption));
		}

		const option = options.find((o) => getIdValue(o) == selectedId); //== is fine i think, so it works with a mix of string and number
		return option ? String(getIdValue(option)) : '-1';
	};

	const selectedValue = getSelectedValue();
	let cssClasses = `biz-select ${className}`;
	const fallbackText = noSelectText ?? '-- Select --'; //should have a translation, but to be honest, it should be on the consumer to provide noSelectText if needed....
	const shouldRenderFallbackOption = selectedValue === '-1' || (!!noSelectText && !shouldAutoSelectSingleOption);

	if (!isValid) {
		cssClasses += ' invalid';
	}

	let paperSxProps: SxProps | undefined;
	if (maxOpenedWidth) {
		paperSxProps = {
			maxWidth: maxOpenedWidth,
			'& .MuiMenuItem-root': {
				whiteSpace: 'normal',
				overflowWrap: 'anywhere',
				wordBreak: 'break-word',
				lineHeight: 1.25,
			},
			// if your items include ListItemText, ensure it can shrink/wrap
			'& .MuiMenuItem-root .MuiListItemText-root': {
				minWidth: 0,
			},
		};
	}

	let selectSxProps: SxProps | undefined;
	if (maxWidth) {
		selectSxProps = {
			maxWidth: maxWidth,
		};
	}

	const select = (
		<Select
			ref={anchor}
			className={cssClasses}
			data-biz-size={size}
			onChange={onChangeHandler}
			value={selectedValue}
			disabled={disabled}
			onOpen={onOpen}
			onClose={onClose}
			name={name}
			inputProps={label ? { 'aria-label': label } : undefined}
			autoWidth={false}
			sx={selectSxProps}
			MenuProps={{
				// Native modal dialogs hide and disable menus portalled outside the dialog.
				container: () => anchor.current?.closest('dialog') ?? document.body,
				PaperProps: {
					sx: paperSxProps,
				},
			}}
		>
			{shouldRenderFallbackOption && !shouldAutoSelectSingleOption && (
				<MenuItem value='-1'>
					<span className='biz-select-item-label'>{fallbackText}</span>
				</MenuItem>
			)}
			{options.map((o) => (
				<MenuItem value={getIdValue(o)} key={getIdValue(o)} disabled={getDisabled?.(o)}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
						<span className='biz-select-item-label'>{getNameValue(o) ?? getName?.(o) ?? 'NO NAME FOUND'}</span>
						{!!getDescription && (
							<>
								<small className='biz-select-item-description' style={{ marginLeft: '5px', fontSize: '0.75rem' }}>
									<i>{getDescription(o)}</i>
								</small>
							</>
						)}
					</div>
				</MenuItem>
			))}
		</Select>
	);
	return label ? (
		<div className='biz-select-field'>
			<span>{label}</span>
			{select}
		</div>
	) : (
		select
	);
}

export function BizNativeSelect<OptionType extends { Id: number | string; Name?: string }>({
	options,
	onChange,
	selectedId,
	disabled,
	noSelectText,
	className,
	getName,
	size,
}: BizSelectProps<OptionType>) {
	function onChangeHandler(event: ChangeEvent<HTMLSelectElement>) {
		const selectedOptionId = event.target.value;
		onChange(options.find((o) => o.Id === (typeof o.Id === 'number' ? +selectedOptionId : selectedOptionId)) ?? null);
	}

	const getSelectedValue = () => {
		const option = options.find((o) => o.Id == selectedId); //== is fine i think, so it works with a mix of string and number
		return option ? String(option.Id) : '-1';
	};

	const selectedValue = getSelectedValue();
	const cssClasses = `biz-select biz-native-select ${className}`;

	return (
		<NativeSelect
			className={cssClasses}
			data-biz-size={size}
			onChange={onChangeHandler}
			value={selectedValue}
			disabled={disabled}
		>
			{noSelectText && <option value='-1'>{noSelectText}</option>}
			{options.map((o) => (
				<option value={o.Id} key={o.Id}>
					{o.Name ?? getName?.(o) ?? 'NO NAME FOUND'}
				</option>
			))}
		</NativeSelect>
	);
}

type HierarchicalOptionType = {
	Id: number | string;
	Name?: string;
	ParentId?: number | string | null;
};

type BizParentSelectProps<OptionType extends HierarchicalOptionType> = {
	size?: BizControlSize;
	getLevelLabel?: (options: OptionType[], level: number) => string;
	options: OptionType[];
	onChange: (option: OptionType | null, parentOption: OptionType | null) => void;
	className?: string;
	childClassName?: string;
	disabled?: boolean;
	noSelectText?: string;
	childNoSelectText?: string;
	getName?: (option: OptionType) => string;
	selectedId?: number | string | null;
	direction?: 'horizontal' | 'vertical';
	formConfig?: FormFieldConfig;
	name?: string;
};

export function BizParentSelect<OptionType extends HierarchicalOptionType>({
	options,
	onChange,
	className,
	childClassName,
	disabled,
	noSelectText,
	childNoSelectText,
	getName,
	getLevelLabel,
	selectedId,
	direction,
	formConfig,
	name: pName,
	size,
}: BizParentSelectProps<OptionType> & { selectedId?: number | string | null }) {
	const [name] = useState(pName ?? getGuid());
	const isValid = formConfig?.state.fields[name]?.isValid ?? true;
	const [selectedOptions, setSelectedOptions] = useState<(OptionType | null)[]>([]);

	const getTopLevelOptions = (): OptionType[] => {
		const allIds = new Set(options.map((o) => o.Id));
		return options.filter((o) => o.ParentId == null || !allIds.has(o.ParentId));
	};

	useEffect(() => {
		return () => {
			//remove field from form state on unmount
			if (formConfig) {
				console.log('removing field from form state:', name);
				formConfig.state.removeField(name);
			}
		};
	}, []);

	useEffect(() => {
		if (!formConfig || !formConfig.validators) return;

		const value = selectedId && selectedId != '-1' ? selectedId.toString() : '';
		const newIsValid = formConfig.state.validateFieldValue(value, formConfig.validators);
		if (newIsValid !== isValid) {
			formConfig.state.setFieldValid(name, newIsValid, formConfig.group);
		}
	}, [selectedId, formConfig?.validators]);

	useEffect(() => {
		if (selectedId == null) {
			setSelectedOptions([]);
			return;
		}

		// Pre-select options based on the selectedId
		const preSelectedOptions: (OptionType | null)[] = [];
		let currentOption = options.find((o) => o.Id === selectedId) || null;

		while (currentOption) {
			preSelectedOptions.unshift(currentOption);
			currentOption = options.find((o) => o.Id === currentOption!.ParentId) || null;
		}

		setSelectedOptions(preSelectedOptions);
	}, [selectedId, options]);

	const handleSelectChange = (level: number, selectedOption: OptionType | null) => {
		const newSelectedOptions = [...selectedOptions];
		newSelectedOptions[level] = selectedOption;

		// Remove selections for deeper levels
		newSelectedOptions.splice(level + 1);

		setSelectedOptions(newSelectedOptions);

		// Notify with the deepest valid selected option
		const deepestValidOption =
			selectedOption && selectedOption.Id !== '-1' && selectedOption.Id !== ''
				? selectedOption
				: (newSelectedOptions[level - 1] ?? null);

		onChange(deepestValidOption, newSelectedOptions[level - 1] ?? null);

		if (formConfig) {
			formConfig.state.setDirty();
		}
	};

	const getOptionsForParent = (parentId: number | string | null) => {
		// Convert parentId to match the format used in options
		const normalizedParentId = parentId === null ? -1 : parentId;
		return options.filter((o) => (o.ParentId ?? -1) === normalizedParentId);
	};

	let cssClasses = `biz-parent-select ${direction === 'horizontal' ? 'horizontal' : 'vertical'} ${className ?? ''}`.trim();

	if (!isValid) {
		cssClasses += ' invalid';
	}

	const topLevelOptions = getTopLevelOptions();

	return (
		<div className={cssClasses}>
			{[null, ...selectedOptions].map((parentOption, level) => {
				const childOptions = level === 0 ? topLevelOptions : getOptionsForParent(parentOption?.Id ?? null);

				// Stop rendering further selects if no child options exist
				if (childOptions.length === 0) return null;

				return (
					<BizSelect
						size={size}
						key={level}
						label={getLevelLabel?.(childOptions, level)}
						options={childOptions}
						onChange={(option) => handleSelectChange(level, option)}
						selectedId={selectedOptions[level]?.Id ?? null}
						disabled={disabled}
						noSelectText={level === 0 ? noSelectText : childNoSelectText}
						className={childClassName}
						getName={getName}
					/>
				);
			})}
		</div>
	);
}
